import { getAgentEditChangedRanges } from '@atlaskit/editor-common/collab-agent-edit-changed-ranges';
import { AGENT_EDIT_CHROME_DATA } from '@atlaskit/editor-common/collab-agent-edit-chrome';
import type { EditorState, Transaction } from '@atlaskit/editor-prosemirror/state';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';

import { isQuickActionConfigItem } from '../../agent-attribution/resolveAgentAttribution';
import { agentEditChromePluginKey } from '../../agent-edit-chrome/plugin-key';
import { getAgentEditChromePresentation } from '../../agent-edit-chrome/presentation';
import { aiSessionContextPluginKey } from '../../ai-session-context/ai-session-context-plugin-key';
import { aiExperienceDecorationPluginKey } from '../../decoration/decoration-plugin-key';
import { purePmInEditorDirectStreamingPluginKey } from '../pluginKey';

/**
 * Stamps the shared post-apply chrome with ranges changed by this applied
 * transaction. The chrome plugin accumulates existing highlights. Session review
 * positions can include unchanged siblings, so they are not highlight input.
 *
 * Lifetime and telepointer visibility are deliberately omitted so the chrome
 * plugin applies its shared static defaults. Reading them from the backend
 * streaming experiment would tie this frontend treatment to that rollout and
 * log an exposure against it.
 */
export function stampUnifiedAgentEditChrome(tr: Transaction, state: EditorState): Transaction {
	if (!isExperimentEnabled('platform_editor_ai_streaming_ux_experience_m1')) {
		return tr;
	}

	// Keep refreshing existing cues even when this transaction adds no new ranges.
	// Do not put their positions in the metadata: the chrome plugin owns mapping
	// them through moves/deletes (and retaining them for streaming replacements).
	const hasExistingRanges = agentEditChromePluginKey
		.getState(state)
		?.ranges.some(({ from, to }) => to > from);
	// This adapter runs at a local AI write boundary, so every applied step belongs
	// to the write. The detector compares each step's before/after documents and
	// maps surviving changes to the final document, ignoring localId-only repair.
	const ranges = getAgentEditChangedRanges(
		tr,
		tr.steps.map((_, index) => index),
		{ requireUniqueSourceIds: true },
	);
	if (!ranges.length && !hasExistingRanges) {
		return tr;
	}

	const decorationPluginState = aiExperienceDecorationPluginKey.getState(state);
	const streamingState = purePmInEditorDirectStreamingPluginKey.getState(state);
	const isQuickAction =
		streamingState?.isQuickAction ||
		isQuickActionConfigItem(decorationPluginState?.configItem?.key);
	// The Quick Action resolver normalizes Rovo's identity ARI to an account ID and stores it as agentId.
	const quickActionAgentIdentityAccountId = isQuickAction
		? aiSessionContextPluginKey.getState(state)?.agentAttribution?.agentId
		: undefined;
	const agentInfo = decorationPluginState?.agentInfo;
	const presentationAgentInfo = quickActionAgentIdentityAccountId
		? { ...agentInfo, agentIdentityAccountId: quickActionAgentIdentityAccountId }
		: agentInfo;

	return tr.setMeta(AGENT_EDIT_CHROME_DATA, {
		...getAgentEditChromePresentation(presentationAgentInfo, {
			fallbackToRovo: isQuickAction,
			preferRovo: isQuickAction,
		}),
		ranges,
	});
}
